/** The header both profile screens wear, and the switch between them.
 *
 * Account and Tokens are two views of one thing, so they share a header
 * rather than each opening with their own: the same avatar, the same name,
 * the same three figures, and a segmented control that moves between them.
 * That control is a pair of links and not a pair of buttons — the two views
 * are addresses, they belong in the browser's history, and `aria-current`
 * says which one is open without a second piece of state to keep in step.
 *
 * The three figures on the right are the account's standing in the shared
 * map, and they are deliberately the merged ones: uploads sent, circuits
 * landed, metres added. What is still pending is on the Account card below,
 * where it can carry its reason.
 */

import { Avatar, SegmentedNav, StatTile } from '@/components/ui';
import { count, metres, shortDate } from '@/lib/format';
import type { AccountStats, User } from '@/lib/types';

export interface ProfileHeaderProps {
  user: User;
  stats: AccountStats;
}

const TABS = [
  { to: '/profile', label: 'Account', end: true },
  { to: '/profile/tokens', label: 'Tokens & installations' },
] as const;

export function ProfileHeader({ user, stats }: ProfileHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <Avatar name={user.display_name} size={52} />

      <div style={{ minWidth: 160 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>{user.display_name}</h1>
        <div
          className="tabular-nums"
          style={{ fontSize: 12.5, color: 'var(--color-neutral-500)' }}
        >
          member since {shortDate(user.created_at)} · role {user.role}
        </div>
      </div>

      <div style={{ marginRight: 'auto' }}>
        <SegmentedNav items={TABS} label="Profile section" />
      </div>

      <div style={{ display: 'flex', gap: 18, textAlign: 'right' }}>
        <StatTile align="right" value={count(stats.uploads)} label="uploads" />
        <StatTile align="right" value={count(stats.tracks_contributed)} label="tracks contributed" />
        <StatTile align="right" value={metres(stats.metres_added)} label="metres you added" />
      </div>
    </div>
  );
}

export default ProfileHeader;
